import { getCorrectTypeColor } from '@repo/lib/quiz/getCorrectTypeColor';
import { QuestionMultiple } from '@repo/lib/quiz/questions/class/QuestionMultiple';
import { PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { QuestionMarkedMultipleForm } from '@/components/features/quiz/question/question-marked/question-multiple';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Markdown } from '@/components/ui/markdown';
import { CorrectIcon } from '@/components/ui/quiz';
import { useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedMultipleProps } from '.';

import { QuestionMarkedMultipleContent } from './QuestionMarkedMultipleContent';

export function QuestionMarkedMultiple(questionMarkedMultipleProps: QuestionMarkedMultipleProps) {
  const { marked, question, showMarked, showQuestion, answer, onEditMark } = questionMarkedMultipleProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();

  const questionObj = useMemo<QuestionMultiple>(() => {
    const qClass = new QuestionMultiple(question, null, marked);
    if (answer) qClass.importAnswer(answer);
    return qClass;
  }, [question, marked, answer]);

  const score: number = questionObj.getPoints();
  const maxPoints: number = questionObj.getMaxPoints();
  const correct: boolean = marked?.answers?.every((answer) => answer.correct) ?? false;
  const correctState = questionObj.correctState();

  if (!showMarked) return null;

  if (!marked || !questionObj.isMarked()) return <QuestionMarkedNoAnswer showMarked={showMarked} />;

  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={maxPoints}
        correct={correct}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content}>
        <QuestionMarkedMultipleContent question={question} />
      </QuestionMarkedQuestionContent>

      {marked.answers && !openForm && (
        <QuestionMarkedUserAnswer>
          <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
          {marked.answers.map((answer) => {
            const foundAnswer = question.answers.find((a) => a.id === answer.id);
            return (
              <div key={answer.id} className="space-y-1">
                <div className="flex items-center gap-1">
                  <CorrectIcon answerState={Boolean(answer.correct)} size={'sm'} />
                  <Markdown
                    content={foundAnswer?.content ?? ''}
                    textClassName={getCorrectTypeColor(Boolean(answer.correct))}
                    imageClassName="max-h-68 rounded-sm"
                    videoClassName="max-h-68 rounded-sm"
                  />
                </div>
                <p className="text-muted-foreground ml-5 text-sm empty:hidden">{answer.explanation}</p>
              </div>
            );
          })}
        </QuestionMarkedUserAnswer>
      )}

      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedMultipleForm onSubmit={onEditMark} onClose={closeFormHandler} maxPoints={maxPoints} marked={questionObj.getMarked()} content={questionObj.getContent()} isOpen={openForm} />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
